'use client'

import { canEditClaim, canDeleteClaim, getDisabledClaimTooltip } from '@repo/utils/claimPermissions'
import { confirm } from '@repo/ui/Confirm'
import { MoreVertIcon } from '@repo/ui/MoreVertIcon'
import { PopupWithBackdrop } from '@repo/ui/PopupWithBackdrop'
import { useClaimsStore } from '@repo/store/useClaimsStore'
import { useExpensesStore } from '@repo/store/useExpensesStore'
import { useOvertimesStore } from '@repo/store/useOvertimesStore'
import { useMenuState, MenuItem } from '@szhsin/react-menu'
import { useNavigate } from 'react-router-dom'
import { useRef } from 'react'
import { useTheme } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'

export const ClaimsMoreMenuCard = ({
  isSubmissionVersion,
  rowData,
  disabled,
  isExpensesTable = false
}: ClaimsMoreMenuCardProps) => {
  const { setIsClaimsDrawerOpen } = useClaimsStore()
  const { deleteExpenseClaimWithLocation } = useExpensesStore()
  const { deleteOvertimeClaim } = useOvertimesStore()
  const [menuState, toggleMenu] = useMenuState({ transition: true })
  const ref = useRef(null)
  const navigate = useNavigate()

  const theme = useTheme()

  const isEditDisabled = !canEditClaim(isSubmissionVersion, rowData?.status)
  const isDeleteDisabled = !canDeleteClaim(isSubmissionVersion, rowData?.status)

  const handleProcessClaim = () => {
    if (isExpensesTable) {
      if (!rowData?.expenseClaimId) return
      navigate(`/accounts/employee-claims/expense-claims/expense-review/${rowData.expenseClaimId}`)
    } else {
      if (!rowData?.overtimeClaimId) return
      navigate(`/accounts/employee-claims/overtime-claims/overtime-review/${rowData.overtimeClaimId}`)
    }
  }

  const handleEditClaim = () => {
    if (isExpensesTable) {
      setIsClaimsDrawerOpen(isExpensesTable, { expenseClaimId: rowData.expenseClaimId, status: rowData.status }, isSubmissionVersion)
    } else {
      setIsClaimsDrawerOpen(isExpensesTable, { overtimeClaimId: rowData.overtimeClaimId, status: rowData.status }, isSubmissionVersion)
    }
    toggleMenu(false)
  }

  const handleDeleteClaim = async () => {
    const proceed = await confirm('Are you sure you want to delete this claim?')
    if (!proceed) return

    if (isExpensesTable) {
      deleteExpenseClaimWithLocation({ expenseClaimId: rowData.expenseClaimId })
    } else {
      deleteOvertimeClaim({ overtimeClaimId: rowData.overtimeClaimId })
    }
    toggleMenu(false)
  }

  return (
    <MUIStack>
      <IconButton onClick={() => toggleMenu(true)} ref={ref} disabled={disabled}>
        <MoreVertIcon sx={{ height: '20px', width: '20px', opacity: disabled ? 0.3 : 1 }} />
      </IconButton>

      <PopupWithBackdrop menuState={menuState} anchorRef={ref} toggleMenu={toggleMenu} className="masterActionMenu">
        {isSubmissionVersion ? (
          <>
            <MUITooltip arrow placement="top" title={isEditDisabled ? getDisabledClaimTooltip('edit') : ''}>
              <span>
                <MenuItem onClick={handleEditClaim} disabled={isEditDisabled}>
                  Edit claim
                </MenuItem>
              </span>
            </MUITooltip>
            <MUITooltip arrow placement="top" title={isDeleteDisabled ? getDisabledClaimTooltip('delete') : ''}>
              <span>
                <MenuItem onClick={handleDeleteClaim} className="szh-menu__item__delete" disabled={isDeleteDisabled}>
                  Delete claim
                </MenuItem>
              </span>
            </MUITooltip>
          </>
        ) : (
          <>
            <MenuItem onClick={handleProcessClaim}>Process claim</MenuItem>
            <MUITooltip arrow placement="top" title={isEditDisabled ? getDisabledClaimTooltip('edit') : ''}>
              <span>
                <MenuItem onClick={handleEditClaim} disabled={isEditDisabled}>
                  Edit claim
                </MenuItem>
              </span>
            </MUITooltip>
          </>
        )}
      </PopupWithBackdrop>
    </MUIStack>
  )
}

type ClaimsMoreMenuCardProps = {
  isSubmissionVersion?: boolean
  rowData?: any
  disabled?: boolean
  isExpensesTable?: boolean
}
